// Сообщение поверх игры: затемнение, надпись и одна кнопка.
//
// Сообщение появляется, когда игра дальше идти не может: связь с бэкендом потеряна, а итог
// спина выдумывать нельзя. Пока оно на экране, игрок видит только его: затемнение растянуто
// на всю видимую область и забирает нажатия себе.
//
// Надпись набрана системным шрифтом. Своего шрифта с буквами в ассетах нет, а сообщение
// об ошибке важнее вида: игрок должен прочесть, что случилось.

import { Container, Graphics, Text } from 'pixi.js';

import type { UiFrame } from '../assets/catalog';
import type { TextureSheet } from '../assets/manager';

import { PlateButton } from './plateButton';
import type { Viewport } from './scene';

/** Насколько затемнение гасит игру под собой. */
const DIM_ALPHA: number = 0.75;

/** Отступ надписи и кнопки от середины сообщения. */
const GAP: number = 90;

/** Высота кегля надписи на кнопке. */
const LABEL_SIZE: number = 36;

/** Сообщение с одной кнопкой. */
export class Notice {
  readonly view: Container = new Container();

  readonly #dim: Graphics = new Graphics();
  readonly #text: Text;
  readonly #button: PlateButton;

  /** Кого разбудить по нажатию кнопки. */
  #onPress: (() => void) | null = null;

  constructor(ui: TextureSheet<UiFrame>, button: string) {
    this.#text = new Text({
      text: '',
      style: { fill: 0xffffff, fontFamily: 'Arial', fontSize: 48, align: 'center' },
    });

    this.#text.anchor.set(0.5);
    this.#text.y = -GAP;

    const faces = { idle: ui.okIdle, hover: ui.okHover, active: ui.okActive };
    this.#button = new PlateButton(faces, { fill: 0xffffff, fontFamily: 'Arial', fontSize: LABEL_SIZE }, () => this.#press());
    this.#button.label = button;
    this.#button.view.y = GAP;
    this.#button.view.visible = false;

    // Затемнение забирает нажатия себе: пока сообщение на экране, игра их не получает.
    this.#dim.eventMode = 'static';

    this.view.visible = false;
    this.view.addChild(this.#dim, this.#text, this.#button.view);
  }

  /** Показывает сообщение и ждёт, пока игрок нажмёт кнопку. */
  ask(text: string): Promise<void> {
    this.#text.text = text;
    this.view.visible = true;
    this.#button.show();

    return new Promise<void>((resolve) => {
      this.#onPress = resolve;
    });
  }

  /** Убирает сообщение, не дожидаясь нажатия. */
  hide(): void {
    this.view.visible = false;
    this.#button.hide();
    this.#onPress = null;
  }

  /** Доводит нажатие кнопки: кадр отгорел, кнопка срабатывает. */
  update(seconds: number): void {
    this.#button.update(seconds);
  }

  /** Меняет только подпись кнопки: открытое сообщение и его ожидание остаются прежними. */
  set buttonLabel(value: string) {
    this.#button.label = value;
  }

  /** Растягивает затемнение на всю видимую область: под сообщением не должно остаться игры. */
  layout(viewport: Viewport): void {
    this.#dim.clear();
    this.#dim.rect(-viewport.width / 2, -viewport.height / 2, viewport.width, viewport.height);
    this.#dim.fill({ color: 0x000000, alpha: DIM_ALPHA });
  }

  #press(): void {
    const pressed: (() => void) | null = this.#onPress;

    this.hide();
    pressed?.();
  }
}
